import React, { useRef, useEffect, useState, useCallback } from 'react'
import styled, { keyframes, css } from 'styled-components'

const API = process.env.REACT_APP_API_URL || ''
const BG = '#060a12'

const SLIDES = [
  {
    mediaKey: 'hero_screen_1', num: '01',
    heading: ["India's Drone", 'Marketplace'],
    tagline: 'Buy and sell drones with complete ease',
    body: 'Join thousands of verified buyers and sellers across India on Flyhub.',
    accent: '#3d9eff',
  },
  {
    mediaKey: 'hero_screen_2', num: '02',
    heading: ['Explore Every', 'Category'],
    tagline: 'Drones, Parts, Accessories & Jobs',
    body: "A complete ecosystem built for India's growing drone industry.",
    accent: '#ff6b35',
  },
  {
    mediaKey: 'hero_screen_3', num: '03',
    heading: ['Rent Drones', '& Equipment'],
    tagline: 'Short-term rentals for every need',
    body: 'Test before you buy. Rent for events, projects, and aerial work.',
    accent: '#00d48e',
  },
  {
    mediaKey: 'hero_screen_4', num: '04',
    heading: ['Become a Seller', 'or Buyer'],
    tagline: 'Trusted platform. Verified listings.',
    body: 'List products or discover your perfect drone — all in one place.',
    accent: '#b06fff',
  },
  {
    mediaKey: 'hero_screen_5', num: '05',
    heading: ['Train. Fly.', 'Earn.'],
    tagline: 'The complete drone professional ecosystem',
    body: 'Courses, pilot jobs, and rentals — every need, one app.',
    accent: '#ff3d71',
  },
]

const TOTAL = SLIDES.length
const N = TOTAL - 1
const SNAPS = SLIDES.map((_, i) => i / N)

function getTextSlide(p) { return Math.min(Math.floor(p * N + 0.001), N) }

const flipIn = keyframes`
  from { transform: rotateY(-85deg) scale(0.85); opacity: 0; }
  to { transform: rotateY(0deg) scale(1); opacity: 1; }
`
const flipOut = keyframes`
  from { transform: rotateY(0deg) scale(1); opacity: 1; }
  to { transform: rotateY(85deg) scale(0.85); opacity: 0; }
`
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`
const dotPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
`

const HeroWrapper = styled.div`
  position: relative;
  height: ${props => props.isMobile ? '100vh' : '600vh'};
  background: ${BG};
  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: radial-gradient(circle, rgba(255,255,255,.03) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
    z-index: 0;
  }
`
const HeroPanel = styled.div`
  position: ${props => props.position === 'fixed' ? 'fixed' : 'absolute'};
  top: ${props => props.position === 'bottom' ? 'auto' : 0};
  bottom: ${props => props.position === 'bottom' ? 0 : 'auto'};
  left: 0; right: 0;
  width: 100%; height: 100vh;
  background: ${BG};
  overflow: hidden;
  z-index: 1;
  @media (max-width: 1024px) {
    position: relative !important;
    top: auto !important;
    bottom: auto !important;
  }
`
const GlowBg = styled.div`
  position: absolute;
  width: clamp(240px, 35vw, 400px);
  height: clamp(240px, 35vw, 400px);
  border-radius: 50%;
  right: 20%; top: 50%;
  transform: translateY(-50%);
  background: ${props => `radial-gradient(circle, ${props.accent}15 0%, transparent 70%)`};
  pointer-events: none;
  z-index: 0;
  transition: background 0.8s ease;
  @media (max-width: 1024px) {
    right: 50%;
    transform: translateY(-50%) translateX(50%);
    width: 200px; height: 200px;
  }
  @media (max-width: 640px) { width: 160px; height: 160px; }
`
const ContentOverlay = styled.div`
  position: absolute;
  inset: 0; z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 clamp(30px, 3vw, 50px);
  gap: 0;
  @media (max-width: 1024px) {
    flex-direction: column;
    justify-content: flex-end;
    padding: 0 20px 80px;
    gap: 35px;
  }
  @media (max-width: 640px) { padding: 0 18px 100px; gap: 30px; }
`
const PhoneSection = styled.div`
  flex: 0 0 auto;
  width: clamp(160px, 18vw, 240px);
  aspect-ratio: 9.5 / 19.5;
  perspective: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: clamp(20px, 2vw, 50px);
  @media (max-width: 1024px) {
    display: flex;
    width: clamp(140px, 55vw, 220px);
    margin: 0 auto;
    order: -1;
  }
  @media (max-width: 640px) { width: clamp(120px, 65vw, 200px); }
`
const PhoneFrame = styled.div`
  width: 100%; height: 100%;
  border-radius: clamp(16px, 2.2vw, 32px);
  background: #0a0a0f;
  border: 1.5px solid #2a2a38;
  box-shadow: 
    0 16px 48px rgba(0,0,0,0.8),
    0 0 0 8px #1a1a24 inset,
    0 0 0 10px #2a2a38 inset;
  position: relative;
  overflow: hidden;
  @media (max-width: 640px) {
    border: 1px solid #2a2a38;
    box-shadow: 
      0 12px 32px rgba(0,0,0,0.7),
      0 0 0 6px #1a1a24 inset,
      0 0 0 8px #2a2a38 inset;
  }
`
const PhoneNotch = styled.div`
  position: absolute;
  top: clamp(6px, 1vw, 12px);
  left: 50%;
  width: 32%;
  height: clamp(14px, 1.8vw, 20px);
  background: #0a0a0f;
  border-radius: 0 0 16px 16px;
  transform: translateX(-50%);
  z-index: 20;
`
const ScreenContainer = styled.div`
  width: 100%; height: 100%;
  border-radius: clamp(14px, 2vw, 30px);
  overflow: hidden;
  position: relative;
  background: #000;
`
const ScreenImage = styled.div`
  position: absolute;
  inset: 0;
  background: ${props => `url('${props.img}') center/cover no-repeat`};
  background-color: #000;
  opacity: ${props => (props.isActive ? 1 : 0)};
  visibility: ${props => (props.isActive ? 'visible' : 'hidden')};
  will-change: opacity;
  animation: ${props =>
    props.animate &&
    (props.isActive
      ? css`${flipIn} 0.65s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards`
      : css`${flipOut} 0.65s cubic-bezier(0.6, 0.04, 0.98, 0.335) forwards`)};
`
const ContentSection = styled.div`
  flex: 0 0 auto;
  width: 100%; max-width: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  pointer-events: all;
  margin-right: clamp(20px, 3vw, 60px);
  @media (max-width: 1024px) {
    max-width: 100%;
    text-align: center;
    align-items: center;
    margin-right: 0;
  }
`
const SlideNum = styled.p`
  font-family: 'Exo 2', sans-serif;
  font-size: clamp(8px, 2.5vw, 11px);
  font-weight: 700;
  letter-spacing: 0.28em;
  color: ${props => props.accent};
  text-transform: uppercase;
  margin: 0 0 clamp(10px, 2vw, 18px) 0;
  opacity: 0.8;
  animation: ${slideUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0s both;
`
const Heading = styled.h2`
  font-family: 'Exo 2', sans-serif;
  font-size: clamp(1.5rem, 5vw, 3.2rem);
  font-weight: 900;
  line-height: 1.08;
  color: #ffffff;
  margin: 0 0 clamp(8px, 2vw, 16px) 0;
  word-break: break-word;
  span { display: block; }
  animation: ${slideUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
  @media (max-width: 640px) { font-size: clamp(1.3rem, 6vw, 2.4rem); }
`
const Tagline = styled.p`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(0.8rem, 2vw, 1rem);
  font-weight: 600;
  color: ${props => props.accent};
  margin: 0 0 clamp(6px, 1.5vw, 12px) 0;
  animation: ${slideUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
`
const Description = styled.p`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(0.8rem, 2.2vw, 0.95rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 clamp(16px, 3.5vw, 30px) 0;
  animation: ${slideUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both;
  @media (max-width: 640px) {
    font-size: clamp(0.75rem, 2.5vw, 0.9rem);
    line-height: 1.5;
  }
`
const CTAButton = styled.button`
  align-self: flex-start;
  font-family: 'Exo 2', sans-serif;
  font-size: clamp(0.7rem, 2vw, 0.85rem);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: clamp(8px, 2vw, 12px) clamp(16px, 3vw, 26px);
  border: 1.5px solid ${props => props.accent};
  background: transparent;
  color: ${props => props.accent};
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  animation: ${slideUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
  white-space: nowrap;
  &:hover {
    background: ${props => props.accent};
    color: ${BG};
    transform: translateY(-2px);
    box-shadow: 0 10px 28px ${props => props.accent}40;
  }
  &:active { transform: translateY(0); }
  svg { width: 10px; height: 10px; stroke-width: 2.5; flex-shrink: 0; }
  @media (max-width: 1024px) { align-self: center; }
  @media (max-width: 640px) {
    padding: clamp(7px, 1.8vw, 10px) clamp(14px, 2.5vw, 22px);
    font-size: clamp(0.65rem, 1.8vw, 0.75rem);
  }
`
const DotsContainer = styled.div`
  display: flex;
  gap: clamp(5px, 1.5vw, 9px);
  margin-top: clamp(20px, 4vw, 36px);
  justify-content: flex-start;
  @media (max-width: 1024px) { justify-content: center; }
  @media (max-width: 640px) {
    gap: clamp(4px, 1.2vw, 7px);
    margin-top: clamp(16px, 3vw, 28px);
  }
`
const Dot = styled.button`
  width: clamp(5px, 1vw, 7px);
  height: clamp(5px, 1vw, 7px);
  border-radius: 50%;
  border: none;
  background: ${props => (props.active ? props.accent : 'rgba(255,255,255,0.15)')};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  padding: 0;
  &:hover {
    background: ${props => props.accent};
    animation: ${dotPulse} 0.4s ease-in-out;
  }
  ${props => props.active && `
    transform: scale(1.4);
    box-shadow: 0 0 10px ${props.accent}50;
  `}
`
const NavButtonsContainer = styled.div`
  position: absolute;
  bottom: clamp(16px, 4vw, 28px);
  left: 50%;
  transform: translateX(-50%);
  display: none;
  gap: clamp(12px, 3.5vw, 20px);
  z-index: 30;
  @media (max-width: 1024px) { display: flex; }
`
const NavButton = styled.button`
  width: clamp(36px, 8vw, 48px);
  height: clamp(36px, 8vw, 48px);
  border-radius: 50%;
  border: 1.5px solid ${props => props.accent};
  background: rgba(6, 10, 18, 0.7);
  color: ${props => props.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  padding: 0;
  -webkit-tap-highlight-color: transparent;
  &:hover {
    background: ${props => props.accent};
    color: ${BG};
    transform: scale(1.1);
  }
  &:active { transform: scale(0.95); }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      background: rgba(6, 10, 18, 0.7);
      color: ${props => props.accent};
      transform: scale(1);
    }
  }
  svg { width: clamp(14px, 3vw, 18px); height: clamp(14px, 3vw, 18px); stroke-width: 2.5; }
`
const BrandLogo = styled.div`
  position: absolute;
  top: clamp(12px, 2.5vw, 24px);
  left: clamp(14px, 3.5vw, 36px);
  z-index: 20;
  pointer-events: none;
`
const BrandSub = styled.p`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(5px, 1.2vw, 8px);
  letter-spacing: 0.3em;
  color: rgba(255, 255, 255, 0.2);
  text-transform: uppercase;
  margin: 0 0 2px 0;
  @media (max-width: 640px) { font-size: clamp(4px, 1.5vw, 6px); }
`
const BrandName = styled.p`
  font-family: 'Exo 2', sans-serif;
  font-size: clamp(10px, 2vw, 16px);
  font-weight: 900;
  color: #fff;
  letter-spacing: 0.06em;
  margin: 0;
  @media (max-width: 640px) { font-size: clamp(9px, 2.5vw, 13px); }
`
const Counter = styled.div`
  position: absolute;
  bottom: clamp(12px, 2.5vw, 24px);
  left: clamp(14px, 3.5vw, 36px);
  font-family: 'Exo 2', sans-serif;
  font-size: clamp(6px, 1.2vw, 9px);
  font-weight: 700;
  letter-spacing: 0.16em;
  color: rgba(255, 255, 255, 0.15);
  z-index: 20;
  strong { color: rgba(255, 255, 255, 0.4); }
  @media (max-width: 640px) { font-size: clamp(5px, 1.5vw, 7px); }
`
const ProgressBar = styled.div`
  position: absolute;
  bottom: 0; left: 0;
  width: 100%; height: 2px;
  background: rgba(255, 255, 255, 0.05);
  z-index: 20;
`
const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.accent};
  width: ${props => `${props.progress * 100}%`};
  transition: width 0.08s linear, background 0.5s ease;
`

export default function FlyhubHero() {
  const wrapperRef = useRef()
  const pRef = useRef(0)
  const slideRef = useRef(0)       // ← tracks current slide index without closure staleness
  const timeoutRef = useRef(null)  // ← so we can cancel in-flight transitions
  const visibleRef = useRef(true)

  const [urls, setUrls] = useState({})
  const [progress, setProgress] = useState(0)
  const [slide, setSlide] = useState(0)
  const [prevSlide, setPrevSlide] = useState(0)
  const [fadeStage, setFadeStage] = useState('in')
  const [animate, setAnimate] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [panelMode, setPanelMode] = useState('top')
  const [showPrevImage, setShowPrevImage] = useState(false)

  const targetRef = useRef(0)
  const animRaf = useRef(null)
  const touchRef = useRef({ startX: 0, startY: 0, startP: 0, active: false, isHoriz: null })

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024)
    fetch(`${API}/api/media/flyhub_hero`)
      .then(r => r.json())
      .then(d => setUrls(d || {}))
      .catch(() => setUrls({}))
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const applyP = useCallback((p) => {
    pRef.current = p
    setProgress(p)
    const next = getTextSlide(p)

    if (next !== slideRef.current) {
      const prev = slideRef.current
      slideRef.current = next  // update ref synchronously — no stale closure

      // Cancel any in-flight transition to prevent blink on rapid changes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
        // Snap previous transition to its end state instantly
        setFadeStage('in')
        setShowPrevImage(false)
      }

      setPrevSlide(prev)
      setSlide(next)
      setShowPrevImage(true)
      setAnimate(true)
      setFadeStage('out')

      timeoutRef.current = setTimeout(() => {
        setFadeStage('in')
        setShowPrevImage(false)
        timeoutRef.current = null
      }, 320)
    }
  }, []) // ← empty deps: reads slideRef (ref, always current), never stale

  const animateTo = useCallback((target) => {
    targetRef.current = target
    if (animRaf.current) cancelAnimationFrame(animRaf.current)
    const tick = () => {
      const d = targetRef.current - pRef.current
      if (Math.abs(d) < 0.001) {
        applyP(targetRef.current)
        animRaf.current = null
        return
      }
      applyP(pRef.current + d * 0.1)
      animRaf.current = requestAnimationFrame(tick)
    }
    animRaf.current = requestAnimationFrame(tick)
  }, [applyP])

  const goTo = useCallback((i) => {
    animateTo(SNAPS[Math.max(0, Math.min(N, i))])
  }, [animateTo])

  // Use slideRef so these callbacks never go stale
  const goNext = useCallback(() => {
    goTo(slideRef.current + 1)
  }, [goTo])

  const goPrev = useCallback(() => {
    goTo(slideRef.current - 1)
  }, [goTo])

  useEffect(() => {
    if (isMobile) return
    const handler = () => {
      const el = wrapperRef.current
      if (!el) return
      visibleRef.current = el.getBoundingClientRect().top < window.innerHeight
      const rect = el.getBoundingClientRect()
      const scrollable = el.offsetHeight - window.innerHeight
      if (scrollable <= 0) return
      if (rect.top > 0) {
        setPanelMode('top')
        applyP(0)
      } else if (-rect.top >= scrollable) {
        setPanelMode('bottom')
        applyP(1)
      } else {
        setPanelMode('fixed')
        applyP(-rect.top / scrollable)
      }
    }
    window.addEventListener('scroll', handler, { passive: true })
    handler()
    return () => window.removeEventListener('scroll', handler)
  }, [isMobile, applyP])

  useEffect(() => {
    if (!isMobile) return
    const el = wrapperRef.current
    if (!el) return
    const onStart = (e) => {
      touchRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startP: pRef.current,
        active: true,
        isHoriz: null,
      }
    }
    const onMove = (e) => {
      if (!touchRef.current.active) return
      const dx = e.touches[0].clientX - touchRef.current.startX
      const dy = e.touches[0].clientY - touchRef.current.startY
      if (touchRef.current.isHoriz === null) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return
        touchRef.current.isHoriz = Math.abs(dx) >= Math.abs(dy)
      }
      if (!touchRef.current.isHoriz) return
      e.preventDefault()
      applyP(Math.max(0, Math.min(1, touchRef.current.startP + (-dx / (window.innerWidth * 0.75)))))
    }
    const onEnd = () => {
      if (!touchRef.current.active) return
      touchRef.current.active = false
      animateTo(SNAPS.reduce((a, b) => Math.abs(b - pRef.current) < Math.abs(a - pRef.current) ? b : a))
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
    }
  }, [isMobile, applyP, animateTo])

  useEffect(() => {
    let timeout
    const handler = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => setIsMobile(window.innerWidth < 1024), 150)
    }
    window.addEventListener('resize', handler)
    return () => { window.removeEventListener('resize', handler); clearTimeout(timeout) }
  }, [])

  const current = SLIDES[slide]
  const prev = SLIDES[prevSlide]

  const currentImg = urls?.[current?.mediaKey]
    ? (urls[current.mediaKey].startsWith('http') ? urls[current.mediaKey] : `${API}${urls[current.mediaKey]}`)
    : 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22800%22%3E%3Crect fill=%22%23000%22 width=%22400%22 height=%22800%22/%3E%3C/svg%3E'

  const prevImg = urls?.[prev?.mediaKey]
    ? (urls[prev.mediaKey].startsWith('http') ? urls[prev.mediaKey] : `${API}${urls[prev.mediaKey]}`)
    : 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22800%22%3E%3Crect fill=%22%23000%22 width=%22400%22 height=%22800%22/%3E%3C/svg%3E'

  return (
    <>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Exo+2:wght@400;700;900&family=Outfit:wght@300;400;500&display=swap" />

      <HeroWrapper ref={wrapperRef} isMobile={isMobile}>
        <HeroPanel position={panelMode}>
          <GlowBg accent={current.accent} />

          <ContentOverlay>
            <ContentSection>
              <SlideNum accent={current.accent}>── {current.num} / 05</SlideNum>
              <Heading>
                {current.heading.map((line, i) => <span key={i}>{line}</span>)}
              </Heading>
              <Tagline accent={current.accent}>{current.tagline}</Tagline>
              <Description>{current.body}</Description>
              <CTAButton accent={current.accent}>
                DOWNLOAD FLYHUB
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </CTAButton>
              <DotsContainer>
                {SLIDES.map((s, i) => (
                  <Dot
                    key={i}
                    active={i === slide}
                    accent={s.accent}
                    onClick={() => goTo(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </DotsContainer>
            </ContentSection>

            <PhoneSection>
              <PhoneFrame>
                <ScreenContainer>
                  {showPrevImage && (
                    <ScreenImage
                      img={prevImg}
                      isActive={fadeStage === 'out'}
                      animate={animate}
                      aria-hidden="true"
                    />
                  )}
                  <ScreenImage
                    img={currentImg}
                    isActive={!showPrevImage || fadeStage === 'in'}
                    animate={animate && showPrevImage}
                  />
                </ScreenContainer>
                <PhoneNotch />
              </PhoneFrame>
            </PhoneSection>
          </ContentOverlay>

          <NavButtonsContainer>
            <NavButton
              accent={current.accent}
              onClick={goPrev}
              disabled={slide === 0}
              aria-label="Previous slide"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </NavButton>
            <NavButton
              accent={current.accent}
              onClick={goNext}
              disabled={slide === N}
              aria-label="Next slide"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </NavButton>
          </NavButtonsContainer>

          <BrandLogo>
            <BrandSub>Aviatricks Aero Labs</BrandSub>
            <BrandName>FLYHUB</BrandName>
          </BrandLogo>

          <Counter>
            <strong>{String(slide + 1).padStart(2, '0')}</strong> / 05
          </Counter>

          <ProgressBar>
            <ProgressFill accent={current.accent} progress={progress} />
          </ProgressBar>
        </HeroPanel>
      </HeroWrapper>
    </>
  )
}